export enum PlayState {
    PLAY = "play",
    PAUSE = "pause"
}

export class AudioPlayer {
    container: HTMLElement;
    audio: HTMLAudioElement;
    playButton: HTMLElement;
    seekSlider: HTMLInputElement;
    durationContainer: HTMLElement;
    currentTimeContainer: HTMLElement;
    outputContainer: HTMLElement;
    playState: string;
    raf: number | null;
    isDraggingSeek: boolean;
    lastTimeUpdate: number;
    mediaMetadata: MediaMetadata | null;
    isMediaSessionSupported: boolean;

    constructor(container: HTMLElement) {
        this.container = container;
        this.audio = this.container.querySelector('audio');
        this.playButton = container.querySelector('[data-audio-play]');
        this.seekSlider = container.querySelector('[data-audio-timeline]') as HTMLInputElement;
        this.durationContainer = container.querySelector('[data-audio-duration]');
        this.currentTimeContainer = container.querySelector('[data-audio-current-time]');
        this.playState = PlayState.PLAY;
        this.raf = null;
        this.isDraggingSeek = false;
        this.lastTimeUpdate = 0;
        this.mediaMetadata = null;
        this.isMediaSessionSupported = 'mediaSession' in navigator;

        this.initializePlayer();
        this.initializeMediaSession();
    }

    initializeMediaSession() {
        if (!this.isMediaSessionSupported) return;

        // Media Session Action Handlers
        navigator.mediaSession.setActionHandler('play', () => {
            this.togglePlay();
        });

        navigator.mediaSession.setActionHandler('pause', () => {
            this.togglePlay();
        });

        navigator.mediaSession.setActionHandler('seekbackward', (details) => {
            this.seekRelative(-(details.seekOffset || 10));
        });

        navigator.mediaSession.setActionHandler('seekforward', (details) => {
            this.seekRelative(details.seekOffset || 10);
        });

        navigator.mediaSession.setActionHandler('seekto', (details) => {
            if (details.fastSeek && 'fastSeek' in this.audio) {
                (this.audio as any).fastSeek(details.seekTime);
            } else {
                this.audio.currentTime = details.seekTime;
            }
        });

        // Position state (for progress bar in media controls)
        this.updatePositionState();

        // Update position state periodically
        this.audio.addEventListener('timeupdate', this.updatePositionState.bind(this));
    }



    updatePositionState() {
        if (!this.isMediaSessionSupported || !navigator.mediaSession.setPositionState) return;

        navigator.mediaSession.setPositionState({
            duration: this.audio.duration || 0,
            playbackRate: this.audio.playbackRate,
            position: this.audio.currentTime
        });
    }

    seekRelative(seconds: number) {
        const newTime = this.audio.currentTime + seconds;
        this.audio.currentTime = Math.max(0, Math.min(newTime, this.audio.duration));
    }

    initializePlayer() {
        // Handle playback state
        this.playButton.addEventListener('click', this.togglePlay.bind(this));
        this.audio.addEventListener('ended', this.handleEndedEvent.bind(this));
        this.seekSlider.addEventListener('input', (e: Event) => {
            const target = e.target as HTMLInputElement;
            const value = parseFloat(target.value);
            this.showRangeProgress(target);
            this.currentTimeContainer.textContent = this.calculateTime(value);
        }, { passive: true });

        this.seekSlider.addEventListener('change', () => {
            this.audio.currentTime = parseFloat(this.seekSlider.value);
            if (!this.audio.paused) {
                if (!this.raf) {
                    this.raf = requestAnimationFrame(this.whilePlaying.bind(this));
                }
            }
        });

        this.updateTrackingPosition();

        // Add timeupdate listener for fallback tracking
        this.audio.addEventListener('timeupdate', () => {
            if (!this.raf && !this.audio.paused) {
                this.updateTrackingPosition();
            }
        });

        // Initialize audio metadata
        if (this.audio.readyState > 0) {
            this.displayDuration();
            this.setSliderMax();
            this.displayBufferedAmount();
        } else {
            this.audio.addEventListener('loadedmetadata', () => {
                this.displayDuration();
                this.setSliderMax();
                this.displayBufferedAmount();
                this.updatePositionState();
            });
        }

        this.audio.addEventListener('progress', this.displayBufferedAmount.bind(this));
        this.audio.addEventListener('play', this.handlePlayEvent.bind(this));
        this.audio.addEventListener('pause', this.handlePauseEvent.bind(this));
        this.audio.addEventListener('ended', this.handleEndedEvent.bind(this));
    }

    handlePlayEvent() {
        this.playState = PlayState.PAUSE;
        this.container.classList.add('playing');
        if (this.isMediaSessionSupported) {
            navigator.mediaSession.playbackState = "playing";
        }
    }

    handlePauseEvent() {
        this.playState = PlayState.PLAY;
        this.container.classList.remove('playing');
        if (this.isMediaSessionSupported) {
            navigator.mediaSession.playbackState = "paused";
        }
    }

    handleEndedEvent() {
        if (this.isMediaSessionSupported) {
            navigator.mediaSession.playbackState = "none";
        }
        this.resetPlayer();
    }

    togglePlay() {
        if (this.playState === PlayState.PLAY) {
            this.audio.play().then(() => {
                this.raf = requestAnimationFrame(this.whilePlaying.bind(this));
            }).catch(error => {
                console.error("Error playing audio:", error);
            });
        } else {
            this.audio.pause();
            if (this.raf) {
                cancelAnimationFrame(this.raf);
                this.raf = null;
            }
        }
    }

    showRangeProgress(rangeInput: HTMLInputElement) {
        if (rangeInput === this.seekSlider) {
            const percentage = (parseFloat(rangeInput.value) / parseFloat(rangeInput.max) * 100);
            this.container.style.setProperty('--seek-before-width', `${percentage}%`);
        } else {
            const percentage = (parseFloat(rangeInput.value) / parseFloat(rangeInput.max) * 100);
            this.container.style.setProperty('--volume-before-width', `${percentage}%`);
        }
    }

    calculateTime(secs: number): string {
        const minutes = Math.floor(secs / 60);
        const seconds = Math.floor(secs % 60);
        const returnedSeconds = seconds < 10 ? `0${seconds}` : `${seconds}`;
        return `${minutes}:${returnedSeconds}`;
    }

    displayDuration() {
        this.durationContainer.textContent = this.calculateTime(this.audio.duration);
    }

    setSliderMax() {
        this.seekSlider.max = this.audio.duration.toString();
        this.seekSlider.step = "0.01";
    }

    displayBufferedAmount() {
        if (this.audio.buffered.length > 0) {
            const bufferedAmount = this.audio.buffered.end(this.audio.buffered.length - 1);
            const percentage = (bufferedAmount / parseFloat(this.seekSlider.max)) * 100;
            this.container.style.setProperty('--buffered-width', `${percentage}%`);
        }
    }

    whilePlaying() {
        const currentTime = this.audio.currentTime;
        this.seekSlider.value = currentTime.toString();
        this.currentTimeContainer.textContent = this.calculateTime(currentTime);
        const percentage = (currentTime / parseFloat(this.seekSlider.max)) * 100;
        this.container.style.setProperty('--seek-before-width', `${percentage}%`);
        this.raf = requestAnimationFrame(this.whilePlaying.bind(this));
    }

    updateTrackingPosition() {
        const currentTime = this.audio.currentTime;

        if (!this.isDraggingSeek) {
            this.seekSlider.value = currentTime.toString();
            this.currentTimeContainer.textContent = this.calculateTime(currentTime);
            const percentage = (currentTime / parseFloat(this.seekSlider.max)) * 100;
            this.container.style.setProperty('--seek-before-width', `${percentage}%`);
        }
    }

    resetPlayer() {
        // Reset audio position
        this.audio.currentTime = 0;

        // Reset UI elements
        this.seekSlider.value = '0';
        this.currentTimeContainer.textContent = this.calculateTime(0);
        this.container.style.setProperty('--seek-before-width', '0%');

        // Update play state
        this.playState = PlayState.PLAY;
        this.container.classList.remove('playing');

        // Cancel animation frame
        if (this.raf) {
            cancelAnimationFrame(this.raf);
            this.raf = null;
        }

        // Update media session
        if (this.isMediaSessionSupported) {
            navigator.mediaSession.playbackState = "none";
            this.updatePositionState();
        }
    }
}